// How Metered Usage requests are sent. The Metered Usage page asks for several big reports at once, and a few of them
// (the summary and the people list) are slow. Sending everything together piled the slow ones onto an already busy server
// and sometimes ended in a gateway timeout (504). So, for Metered Usage requests only:
//   1. Wave loading: at most MAX_AT_ONCE run at the same time, the quick ones first and the slow ones after them, so the
//      quick cards fill in without waiting behind the slow reports.
//   2. A quiet retry: a read that fails because the gateway gave up or the connection dropped is tried again after a short
//      wait (the server often finished the work and has it ready by then).
// Everything else the app sends is left alone.

export const MAX_AT_ONCE = 2;
// Waits before the 1st and 2nd retry, in milliseconds.
export const RETRY_DELAYS_MS = [4000, 10000];

// "heavy": the slow reports (the summary, the paged people list). "light": every other Metered Usage read. null: not a
// Metered Usage request, or an export (exports are jobs the person asked for, so they are never held back).
export const classifyMeteredRequest = (url) => {
  const path = String(url || "").split("?")[0];
  if (!/\/users\/metered-usage(\/|$)/.test(path)) return null;
  if (/\/export(\/|$)/.test(path)) return null;
  if (/\/users\/metered-usage(\/enterprise)?\/(summary|users)$/.test(path)) return "heavy";
  return "light";
};

const PRIORITY = { light: 1, heavy: 2 };

// A small queue: `limit` jobs at a time, lowest priority number first, first come first served within a priority.
//
// Nothing starts the instant it is asked for. A page asks for all its reports in one go, so the queue waits a moment (one
// tick) for the whole burst to arrive and only then starts the first ones, in priority order. Without that, whichever
// request was asked for first (the slow summary) would start before the quick ones had even been seen.
export const createScheduler = (limit = MAX_AT_ONCE) => {
  let running = 0;
  let sequence = 0;
  let pumpQueued = false;
  const waiting = [];
  const pump = () => {
    while (running < limit && waiting.length) {
      waiting.sort((a, b) => a.priority - b.priority || a.order - b.order);
      const job = waiting.shift();
      running += 1;
      Promise.resolve()
        .then(job.run)
        .then(job.resolve, job.reject)
        .finally(() => {
          running -= 1;
          pump();
        });
    }
  };
  const startSoon = () => {
    if (pumpQueued) return;
    pumpQueued = true;
    setTimeout(() => {
      pumpQueued = false;
      pump();
    }, 0);
  };
  return {
    schedule: (priority, run) =>
      new Promise((resolve, reject) => {
        waiting.push({ priority, order: sequence++, run, resolve, reject });
        startSoon();
      }),
    counts: () => ({ running, waiting: waiting.length }),
  };
};

// A failure worth trying again: the gateway timed out or was unavailable, or the connection dropped (a gateway error often
// reaches the browser without the headers that let it read the reply, which shows up as a network error).
export const isRetryable = (error) =>
  Boolean(error) && (error.status === 502 || error.status === 503 || error.status === 504 || error.status === "FETCH_ERROR" || error.status === "TIMEOUT_ERROR");

const wait = (ms, signal) =>
  new Promise((resolve) => {
    if (signal?.aborted) return resolve();
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener?.("abort", () => {
      clearTimeout(timer);
      resolve();
    });
  });

const defaultScheduler = createScheduler();

// Wraps the app's base query. Anything that is not a Metered Usage request goes straight through, untouched.
export const withMeteredScheduling = (baseQuery, { scheduler = defaultScheduler, retryDelays = RETRY_DELAYS_MS, sleep = wait } = {}) =>
  async (args, api, extraOptions) => {
    const url = typeof args === "string" ? args : args?.url;
    const kind = classifyMeteredRequest(url);
    if (!kind) return baseQuery(args, api, extraOptions);

    const method = String(typeof args === "string" ? "GET" : args?.method || "GET").toUpperCase();
    for (let attempt = 0; ; attempt += 1) {
      // Each try waits its turn in the queue, so a retry never jumps ahead of the quick requests.
      const result = await scheduler.schedule(PRIORITY[kind], () => baseQuery(args, api, extraOptions));
      // Only reads are repeated: repeating a request that changes something could do it twice.
      const again = method === "GET" && isRetryable(result?.error) && attempt < retryDelays.length && !api?.signal?.aborted;
      if (!again) return result;
      await sleep(retryDelays[attempt], api?.signal);
    }
  };
